import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

/**
 * A button, currently only used for the Review Timer. The most frequently used button for ePermits WC is <fluent-button>.
 */
@customElement('ep-button')
export class EPButton extends LitElement {

  @property({ type: Boolean, reflect: true })
  disabled: boolean = false;

  override connectedCallback() {
    super.connectedCallback(); 
    // Use "capture" because we want this Event handler to be called before any others set above it.
    // Otherwise, we can't cancel the event in time when the button is disabled!
    this.addEventListener('click', this._handleClick, { capture: true });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._handleClick, { capture: true });
  }

  private _handleClick(event: MouseEvent) {
    if (this.disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  render() {
    return html`
      <button class="button" ?disabled=${this.disabled}>
        <slot name="leading"></slot>
        <slot></slot>
        <slot name="trailing"></slot>
      </button>
    `
  }

  static styles = css`
    :host {
      display: inline-flex;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      --container-height: 40px;
      --padding-horizontal: 24px;
      --border-radius: 9999px;
      --font: 500 0.875rem / 1.25rem var(--font-prose);
      --color: rgba(255,255,255,0.8);
      --color-disabled: rgba(0, 0, 0, 0.26);
      --background-color: rgb(49, 56, 133);
      --background-color-disabled: rgba(0, 0, 0, 0.12);
      --background-color-hover: rgb(255,255,255);
      --background-color-interact: rgb(255,255,255);
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-family: var(--font-prose);
    }

    /* Base styles */
    .button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      min-inline-size: 64px;
      border: none;
      outline: none;
      user-select: none;
      -webkit-appearance: none;
      vertical-align: middle;
      background: rgba(0,0,0,0);
      text-decoration: none;
      inline-size: 100%;
      position: relative;
      z-index: 0;
      block-size: var(--container-height);
      font-weight: inherit;
      font-size: inherit;
      line-height: inherit;
      font-family: inherit;
      color: var(--color);
      padding-inline-start: var(--padding-horizontal);
      padding-inline-end: var(--padding-horizontal);
      border-radius: var(--border-radius);
      gap: 8px;
      /* background-color: var(--background-color); */
      transition: opacity 15ms linear, background-color 15ms linear, transform 125ms;
    }
    .button:not(:disabled) {
      cursor: pointer;
    }
    .button:disabled {
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Background color */
    .button::before {
      z-index: -2;
      background-color: var(--background-color);
      border-radius: inherit;
      content: "";
      inset: 0;
      position: absolute;
    }
    .button::after {
      z-index: -1;
      background-color: var(--background-color-hover);
      opacity: 0.0;
      border-radius: inherit;
      content: "";
      inset: 0;
      position: absolute;
      transition: opacity 280ms linear, background-color 280ms linear;
    }
    .button:not(:disabled)hover::after {
      background-color: var(--background-color-hover);
      /* opacity: 0.1; */
    }
    .button:not(:disabled):active::after {
      background-color: var(--background-color-interact);
      /* opacity: 0.2; */
    }
    .button:disabled {
      color: var(--color-disabled);
    }
    .button:disabled::before {
      background-color: var(--background-color-disabled);
    }

    /* Interactivity */
    .button:not(:disabled):hover {
      transform: scale(1.05);
    }
    .button:not(:disabled):active {
      transform: scale(0.925);
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-button': EPButton
  }
}
